/**
 * Where an item is, on its page and in a quick look: its breadcrumb (each folder a link, a hidden one "…"), and on its
 * page, when its sharing isn't its folder's, the mark saying so with "Match the folder". "Unpack here…" for a zip.
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, Job, SameTitle, SpaceOut, Unpacked } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { pathText } from "../../lib/folders.ts";
import { followJob } from "../../lib/jobs.ts";
import { spaceApi } from "../../lib/paths.ts";
import { bump, useAction } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Select } from "../../ui/form.tsx";
import { Breadcrumb } from "./breadcrumb.tsx";
import { ItemDrift } from "./drift.tsx";
import { useFolder } from "./listing.ts";

/** Its breadcrumb; `drift`, its sharing compared with its folder's (its page), `onChange` after matching it. */
export function ItemPlace(p: { space: SpaceOut; item: ItemDetail; drift?: boolean; onChange?: () => void }) {
  const { item } = p;
  // Its folder's sharing, to compare (only where the mark is shown).
  const folder = useFolder(p.space, p.drift ? (item.folder ?? null) : null);
  if (item.folder_path.length === 0) {
    return null;
  }
  return (
    <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
      <Breadcrumb space={p.space} path={item.folder_path} small={true} />
      {p.drift && folder.data ? (
        <ItemDrift space={p.space} folder={folder.data} item={item} onDone={() => p.onChange?.()} />
      ) : null}
    </div>
  );
}

/** "Unpack here…": the zip's files into its folder (its directories as folders, merged into those there by name). */
export function UnpackDialog(p: { space: SpaceOut; item: ItemDetail; open: boolean; onClose: () => void }) {
  const { item } = p;
  const [same, setSame] = useState<SameTitle>("keep");
  const where = item.folder_path.length > 0 ? pathText(item.folder_path) : t("folders.root");
  const [run, busy] = useAction(async () => {
    const r = await api.post<Unpacked | Job>(spaceApi(p.space, `/items/${item.id}/unpack`), { same_title: same });
    if ("action" in r) {
      await followJob(p.space, r);
    } else {
      toast(t("folders.unpackedInto", { count: r.added, name: where }));
      if (r.skipped > 0) {
        toast(t("folders.skipped", { count: r.skipped }));
      }
    }
    bump();
    p.onClose();
  });
  return (
    <Dialog open={p.open} onClose={p.onClose} title={t("folders.unpackTitle", { name: item.title ?? "" })}>
      <p class="text-sm text-zinc-700">{t("folders.unpackWhat", { name: where })}</p>
      <Field label={t("folders.sameTitle")}>
        <Select
          value={same}
          onValue={(v) => setSame(v as SameTitle)}
          options={[
            { value: "keep", label: t("folders.keepBoth") },
            { value: "replace", label: t("folders.replace") },
            { value: "skip", label: t("folders.skipIt") },
          ]}
        />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" busy={busy} onClick={() => run()}>
          {t("folders.unpack")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
